import { useState } from 'react';
import type { Asset } from '../types/mexc';
import { Wallet, TrendingUp, TrendingDown, DollarSign, Eye, EyeOff } from 'lucide-react';

interface AssetsSummaryProps {
  assets: Asset[];
  isLoading: boolean;
}

export function AssetsSummary({ assets, isLoading }: AssetsSummaryProps) {
  const [showZeroBalances, setShowZeroBalances] = useState(false);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 animate-pulse">
            <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
          </div>
        ))}
      </div>
    );
  }

  // Filter assets based on zero balance preference
  const filteredAssets = showZeroBalances
    ? assets
    : assets.filter(asset => {
        const totalValue = (asset.availableBalance || 0) + (asset.positionMargin || 0) + (asset.frozenBalance || 0);
        return totalValue > 0;
      });

  // Calculate totals (always use all assets for totals, not just filtered ones)
  const totalBalance = assets.reduce((sum, asset) => sum + (asset.availableBalance || 0), 0);
  const totalMargin = assets.reduce((sum, asset) => sum + (asset.positionMargin || 0), 0);
  const totalFrozen = assets.reduce((sum, asset) => sum + (asset.frozenBalance || 0), 0);
  const totalEquity = totalBalance + totalMargin;

  const summaryCards: Array<{
    title: string;
    value: number;
    icon: typeof Wallet;
    color: string;
    change: number | null;
  }> = [
    {
      title: 'Total Equity',
      value: totalEquity,
      icon: Wallet,
      color: 'primary',
      change: null,
    },
    {
      title: 'Available Balance',
      value: totalBalance,
      icon: DollarSign,
      color: 'success',
      change: null,
    },
    {
      title: 'Position Margin',
      value: totalMargin,
      icon: TrendingUp,
      color: 'warning',
      change: null,
    },
    {
      title: 'Frozen Balance',
      value: totalFrozen,
      icon: TrendingDown,
      color: 'danger',
      change: null,
    },
  ];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((card) => {
          const Icon = card.icon;
          
          return (
            <div
              key={card.title}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 border border-gray-200 dark:border-gray-700"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                    {card.title}
                  </p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">
                    {formatCurrency(card.value)}
                  </p>
                  {card.change !== null && card.change !== undefined && (
                    <p className={`text-sm mt-1 ${
                      card.change > 0 
                        ? 'text-success-600 dark:text-success-400' 
                        : 'text-danger-600 dark:text-danger-400'
                    }`}>
                      {card.change > 0 ? '+' : ''}{card.change.toFixed(2)}%
                    </p>
                  )}
                </div>
                <div className={`
                  p-3 rounded-full
                  ${card.color === 'primary' ? 'bg-primary-100 dark:bg-primary-900/20' : ''}
                  ${card.color === 'success' ? 'bg-success-100 dark:bg-success-900/20' : ''}
                  ${card.color === 'warning' ? 'bg-warning-100 dark:bg-warning-900/20' : ''}
                  ${card.color === 'danger' ? 'bg-danger-100 dark:bg-danger-900/20' : ''}
                `}>
                  <Icon className={`
                    w-6 h-6
                    ${card.color === 'primary' ? 'text-primary-600 dark:text-primary-400' : ''}
                    ${card.color === 'success' ? 'text-success-600 dark:text-success-400' : ''}
                    ${card.color === 'warning' ? 'text-warning-600 dark:text-warning-400' : ''}
                    ${card.color === 'danger' ? 'text-danger-600 dark:text-danger-400' : ''}
                  `} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Detailed Assets */}
      {assets.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Asset Details
              </h3>
              {!showZeroBalances && filteredAssets.length < assets.length && (
                <span className="text-xs bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full">
                  {filteredAssets.length} of {assets.length} shown
                </span>
              )}
            </div>
            <button
              onClick={() => setShowZeroBalances(!showZeroBalances)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-colors"
              title={showZeroBalances ? "Hide zero balances" : "Show zero balances"}
            >
              {showZeroBalances ? (
                <>
                  <EyeOff className="w-4 h-4" />
                  <span className="text-gray-700 dark:text-gray-300">Hide Zero</span>
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4" />
                  <span className="text-gray-700 dark:text-gray-300">Show Zero</span>
                </>
              )}
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Currency
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Available
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Position Margin
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Frozen
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredAssets.map((asset) => (
                  <tr key={asset.currency} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-full flex items-center justify-center mr-3">
                          <span className="text-xs font-bold text-gray-600 dark:text-gray-300">
                            {asset.currency.slice(0, 2)}
                          </span>
                        </div>
                        <span className="font-medium text-gray-900 dark:text-white">
                          {asset.currency}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                      {formatCurrency(asset.availableBalance)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                      {formatCurrency(asset.positionMargin)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 dark:text-white">
                      {formatCurrency(asset.frozenBalance)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(asset.availableBalance + asset.positionMargin + asset.frozenBalance)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
